import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import * as yup from 'yup'
import VerificationCodeInput from 'react-input-verification-code'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'

import PhoneNumberField from '@/components/formik/fields/PhoneNumberField'

import { StepBaseProps } from '../types'

export type ConfirmPhoneNumberStepValues = {
  phone: string
  code: string
}

export type ConfirmPhoneNumberStepProps = StepBaseProps & {
  initialValues: ConfirmPhoneNumberStepValues
  onSendPhoneNumber: (phone: string) => void
  onVerifyPhoneNumber: (code: string) => void
  status: 'initial' | 'pending'
}

const verificationCodeLength = 6

export function ConfirmPhoneNumberStep(props: ConfirmPhoneNumberStepProps) {
  const { initialValues, isSubmitting = false, onSendPhoneNumber, onVerifyPhoneNumber, status = 'initial' } = props
  const { t } = useTranslation()
  const theme = useTheme()

  const [code, setCode] = useState<string>('')

  const handleSubmit = (values: ConfirmPhoneNumberStepValues) => {
    onSendPhoneNumber(values.phone)
  }

  const handleVerifyPhoneNumber = () => {
    onVerifyPhoneNumber(code)
  }

  return (
    <Box>
      {status === 'initial' && (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={yup.object().shape({
            phone: yup.string().required()
          })}
        >
          {({ isValid }) => (
            <Form>
              <Stack spacing={4}>
                <PhoneNumberField label={t('field.phone.label')} name="phone" />
                <LoadingButton disabled={!isValid} loading={isSubmitting} type="submit" variant="contained">
                  {t('common:button.send.label')}
                </LoadingButton>
              </Stack>
            </Form>
          )}
        </Formik>
      )}
      {status === 'pending' && (
        <Stack spacing={4} alignItems="center">
          <Box
            sx={{
              margin: '0 auto',
              '--ReactInputVerificationCode-itemWidth': '30px',
              '--ReactInputVerificationCode-itemHeight': '30px',
              '--ReactInputVerificationCode-itemSpacing': '10px',
              [theme.breakpoints.up('sm')]: {
                '--ReactInputVerificationCode-itemWidth': '50px',
                '--ReactInputVerificationCode-itemHeight': '50px',
                '--ReactInputVerificationCode-itemSpacing': '16px'
              }
            }}
          >
            <VerificationCodeInput
              autoFocus
              length={verificationCodeLength}
              onChange={(value) => setCode(value.slice(0, verificationCodeLength))}
              value={code}
            />
          </Box>
          <LoadingButton disabled={code.includes('·')} loading={isSubmitting} onClick={handleVerifyPhoneNumber} variant="contained">
            {t('common:button.verify.label')}
          </LoadingButton>
        </Stack>
      )}
    </Box>
  )
}

export default ConfirmPhoneNumberStep